import React, { useState } from 'react';
import {
  Settings,
  Server,
  CheckCircle,
  Save,
  Activity,
  AlertCircle
} from 'lucide-react';
import { useTelemetry } from '../context/TelemetryContext';

export default function Setting() {
  const { isLiveBackend, forceMock, setForceMock, loadDashboardData } = useTelemetry();

  const [settings, setSettings] = useState({
    backendUrl: 'http://localhost:8145',
    apiPrefix: '/api/v1',
    authType: 'Basic Auth',
    username: 'admin',
    pollingInterval: 6,
    defaultMno: 'ALL',
    timeoutSeconds: 20,
    enableDetailedPayloads: true,
  });

  const [savedMessage, setSavedMessage] = useState(false);

  const handleChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    setSavedMessage(true);
    setTimeout(() => setSavedMessage(false), 3000);
    loadDashboardData();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* Header Banner */}
      <div className="glass-card" style={{ padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #F84122 0%, #E65100 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(248, 65, 34, 0.35)'
          }}>
            <Settings size={20} color="#FFFFFF" />
          </div>
          <div>
            <h1 style={{ fontSize: '18px', fontWeight: '800', margin: 0, color: 'var(--text-primary)' }}>
              Engine & Telemetry Settings
            </h1>
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '2px 0 0 0' }}>
              Configure upstream USSD mediator integration parameters, telemetry stream, and local test modes
            </p>
          </div>
        </div>
      </div>

      {savedMessage && (
        <div style={{
          padding: '14px 18px',
          background: 'rgba(16, 185, 129, 0.12)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: 'var(--radius-md)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          color: '#059669',
          fontSize: '13px',
          fontWeight: '700'
        }}>
          <CheckCircle size={18} />
          <span>Configuration preferences updated successfully!</span>
        </div>
      )}

      {/* Settings Form */}
      <div className="two-col-grid">
        
        {/* Connection & Auth */}
        <div className="glass-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '18px' }}>
            <Server size={18} color="var(--accent-orange)" />
            <h2 style={{ fontSize: '15px', fontWeight: '800', margin: 0 }}>Mediator Connection</h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: '700' }}>
                Upstream Engine Base URL
              </label>
              <input
                type="text"
                value={settings.backendUrl}
                onChange={(e) => handleChange('backendUrl', e.target.value)}
                style={{
                  width: '100%',
                  background: '#F8F9FA',
                  border: '1px solid var(--border-medium)',
                  color: 'var(--text-primary)',
                  padding: '10px 14px',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '13px',
                  fontFamily: 'var(--font-mono)'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: '700' }}>
                API Endpoint Prefix
              </label>
              <input
                type="text"
                value={settings.apiPrefix}
                onChange={(e) => handleChange('apiPrefix', e.target.value)}
                style={{
                  width: '100%',
                  background: '#F8F9FA',
                  border: '1px solid var(--border-medium)',
                  color: 'var(--text-primary)',
                  padding: '10px 14px',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '13px',
                  fontFamily: 'var(--font-mono)'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: '700' }}>
                Authentication Type
              </label>
              <select
                className="custom-select"
                style={{ width: '100%', padding: '10px 14px' }}
                value={settings.authType}
                onChange={(e) => handleChange('authType', e.target.value)}
              >
                <option value="Basic Auth">HTTP Basic Authentication (admin:password)</option>
                <option value="Bearer">JWT / Bearer Token</option>
                <option value="Mutual TLS">Mutual TLS (mTLS)</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px', background: '#F8FAFC', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>Simulated / Mock Mode</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Fall back to internal mock generator when mediator engine is offline</div>
              </div>
              <button
                type="button"
                onClick={() => setForceMock(!forceMock)}
                className={`btn-${forceMock ? 'primary' : 'secondary'}`}
                style={{ padding: '6px 14px', fontSize: '12px' }}
              >
                {forceMock ? 'Enabled (Mock)' : 'Disabled (Live)'}
              </button>
            </div>
          </div>
        </div>

        {/* Telemetry & Sampling Rules */}
        <div className="glass-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '18px' }}>
            <Activity size={18} color="var(--accent-orange)" />
            <h2 style={{ fontSize: '15px', fontWeight: '800', margin: 0 }}>Telemetry Polling & Limits</h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: '700' }}>
                Real-time Polling Interval (Seconds)
              </label>
              <input
                type="number"
                min="2"
                max="60"
                value={settings.pollingInterval}
                onChange={(e) => handleChange('pollingInterval', parseInt(e.target.value) || 6)}
                style={{
                  width: '100%',
                  background: '#F8F9FA',
                  border: '1px solid var(--border-medium)',
                  color: 'var(--text-primary)',
                  padding: '10px 14px',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '13px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: '700' }}>
                USSD Dialogue Inactivity Timeout (Seconds)
              </label>
              <input
                type="number"
                min="5"
                max="120"
                value={settings.timeoutSeconds}
                onChange={(e) => handleChange('timeoutSeconds', parseInt(e.target.value) || 20)}
                style={{
                  width: '100%',
                  background: '#F8F9FA',
                  border: '1px solid var(--border-medium)',
                  color: 'var(--text-primary)',
                  padding: '10px 14px',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '13px'
                }}
              />
            </div>

            {/* Diagnostic Box */}
            <div style={{
              padding: '16px',
              borderRadius: 'var(--radius-md)',
              background: isLiveBackend ? 'rgba(16, 185, 129, 0.08)' : 'rgba(248, 65, 34, 0.08)',
              border: `1px solid ${isLiveBackend ? 'rgba(16, 185, 129, 0.25)' : 'rgba(248, 65, 34, 0.25)'}`,
              marginTop: '8px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                {isLiveBackend ? <CheckCircle size={16} color="var(--accent-emerald)" /> : <AlertCircle size={16} color="var(--accent-orange)" />}
                <strong style={{ fontSize: '13px', color: isLiveBackend ? 'var(--accent-emerald)' : 'var(--accent-orange)' }}>
                  {isLiveBackend ? 'Backend Mediator is Online' : 'Backend Mediator is Standby / Offline'}
                </strong>
              </div>
              <p style={{ fontSize: '11px', color: 'var(--text-secondary)', margin: 0 }}>
                {isLiveBackend
                  ? 'Active session telemetry, MongoDB flow metrics, and MNO configurations are streaming live.'
                  : 'UI is running in simulated telemetry mode with synthetic traffic generators.'}
              </p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
              <button className="btn-primary" onClick={handleSave}>
                <Save size={15} />
                <span>Save Preferences</span>
              </button>
            </div>

          </div>
        </div>

      </div>

    </div>
  );
}